import { useState } from "react";
import { archiveCollections, archiveIndexRows } from "@/mocks/home";

const levelStyle: Record<string, string> = {
  已归档: "bg-accent-100 text-accent-900 border-accent-300",
  整理中: "bg-secondary-100 text-secondary-900 border-secondary-300",
};

export default function ArchiveIndex() {
  const [activeNo, setActiveNo] = useState(archiveCollections[0].no);
  const [filter, setFilter] = useState("全部");

  const filters = ["全部", "已归档", "整理中"];
  const rows =
    filter === "全部"
      ? archiveIndexRows
      : archiveIndexRows.filter((row) => row.level === filter);

  return (
    <section id="index" className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4 border-b border-background-200 pb-5">
          <div>
            <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-primary-600">
              <i className="ri-list-check-3 text-sm" />
              Archive Index
            </span>
            <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              档案总索引 · 合集优先
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-foreground-600">
              所有条目按编号归并，合集先于零散条目呈现；任一编号都可回溯到原始素材的标注位置。
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-1 py-1">
            {filters.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setFilter(item)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  filter === item
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-12">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-5">
            {archiveCollections.map((col) => (
              <button
                key={col.no}
                type="button"
                onClick={() => setActiveNo(col.no)}
                className={`group flex flex-col overflow-hidden rounded-lg border text-left transition-colors ${
                  activeNo === col.no
                    ? "border-primary-400 bg-primary-50"
                    : "border-background-200 bg-background-100 hover:border-primary-300"
                }`}
              >
                <div className="relative w-full overflow-hidden" style={{ height: 120 }}>
                  <img
                    src={col.image}
                    alt={`${col.title} 合集封面`}
                    title={`${col.title} · 黑料大全合集索引`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute right-2 top-2 font-heading text-xl font-black text-background-50 drop-shadow">
                    {col.no}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading text-sm font-bold text-foreground-950">{col.title}</h3>
                    <span className="shrink-0 rounded bg-background-200 px-1.5 py-0.5 text-[11px] text-foreground-700">
                      {col.count} 条
                    </span>
                  </div>
                  <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-foreground-600">{col.desc}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {col.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded bg-accent-100 px-1.5 py-0.5 text-[11px] text-accent-900"
                      >
                        {tag}
                      </span>
                    ))}
                    <span className="ml-auto text-[11px] text-foreground-500">{col.updated}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-7">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <div className="hidden grid-cols-12 gap-2 border-b border-background-200 bg-background-200/60 px-4 py-3 text-[12px] font-medium text-foreground-700 sm:grid">
                <span className="col-span-3">档案编号</span>
                <span className="col-span-3">所属话题</span>
                <span className="col-span-2">核验状态</span>
                <span className="col-span-2 text-center">节点数</span>
                <span className="col-span-2 text-right">整理日期</span>
              </div>
              <ul className="divide-y divide-background-200">
                {rows.map((row) => (
                  <li
                    key={row.no}
                    className="grid grid-cols-2 gap-2 px-4 py-3.5 text-sm transition-colors hover:bg-background-50 sm:grid-cols-12 sm:items-center"
                  >
                    <span className="col-span-1 flex items-center gap-2 font-label font-semibold text-primary-700 sm:col-span-3">
                      <i className="ri-archive-drawer-line text-base text-primary-500" />
                      {row.no}
                    </span>
                    <span className="col-span-1 text-right text-foreground-700 sm:col-span-3 sm:text-left">
                      {row.topic}
                    </span>
                    <span className="col-span-1 sm:col-span-2">
                      <span
                        className={`inline-block rounded border px-2 py-0.5 text-[11px] ${
                          levelStyle[row.level] ?? "border-background-300 text-foreground-600"
                        }`}
                      >
                        {row.level}
                      </span>
                    </span>
                    <span className="col-span-1 text-right text-foreground-600 sm:col-span-2 sm:text-center">
                      {row.nodes}
                    </span>
                    <span className="col-span-2 text-foreground-500 sm:col-span-2 sm:text-right">
                      {row.date}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between border-t border-background-200 px-4 py-3 text-xs text-foreground-500">
                <span>当前展示 {rows.length} 条索引</span>
                <a href="#index" className="flex items-center gap-1 text-primary-700 hover:text-primary-800">
                  查看完整索引
                  <i className="ri-arrow-right-line text-sm" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}